import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router'
import { fetchAuthSession } from 'aws-amplify/auth'
import { UserAuthService } from './user-auth.service'
import { first } from 'rxjs/operators'
import { Observable, Observer } from 'rxjs'
import { StateService } from '../common/services/state.service'
import { LoaderService } from './loader.service'
import { cxDomain, BOOKING_TYPE_MINOR, BOOKING_TYPE_KEY } from '../common/utilities/constants'
@Injectable({
  providedIn: 'root'
})
export class AuthGuardLoginService {
  redirectRegister: boolean = false
  ssoRedirect: boolean = false
  constructor (private router: Router, private authService: UserAuthService, private stateService: StateService, private loaderService: LoaderService) {}

  canActivate (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    this.loaderService.show()
    // Allow agent verification page without auth check
    if (state.url.startsWith('/agent-verification')) {
      this.loaderService.hide()
      return new Observable<boolean | UrlTree>((observer) => {
        observer.next(true)
        observer.complete()
      })
    }
    const queryParams = route?.queryParamMap
    const ssoRedirect = queryParams.get('ssoRedirect') || ''
    if (ssoRedirect) {
      try {
        const ieltsUrl = new URL(cxDomain).hostname
        const redirectUrl = new URL(ssoRedirect).hostname
        if (ieltsUrl === redirectUrl) {
          this.ssoRedirect = true
          this.authService.setCookie('referrerUrl', ssoRedirect)
        }
      } catch (error) {
        this.ssoRedirect = false
      }
    }
    if (queryParams.keys.length) {
      sessionStorage.removeItem(BOOKING_TYPE_KEY)
      const bookingType = queryParams.get(BOOKING_TYPE_KEY) || ''
      if (bookingType === BOOKING_TYPE_MINOR) sessionStorage.setItem(BOOKING_TYPE_KEY, BOOKING_TYPE_MINOR)
    }

    return new Observable<boolean | UrlTree>((observer) => {
      fetchAuthSession()
        .then((session: any) => this.handleAuthSession(session, state, observer))
        .catch(() => this.handleAuthError(state, observer))
    })
  }

  private handleAuthSession (session: any, state: RouterStateSnapshot, observer: Observer<boolean | UrlTree>): void {
    if (!session?.tokens?.idToken) {
      this.handleNoToken(state, observer)
      return
    }

    if (this.shouldAllowRegisterRedirect(state)) {
      this.completeRegisterRedirect(observer)
      return
    }

    this.processUserDetails(observer)
  }

  private shouldAllowRegisterRedirect (state: RouterStateSnapshot): boolean {
    return this.redirectRegister && state.url === '/register'
  }

  private completeRegisterRedirect (observer: Observer<boolean | UrlTree>): void {
    this.redirectRegister = false
    this.loaderService.hide()
    observer.next(true)
    observer.complete()
  }

  private processUserDetails (observer: Observer<boolean | UrlTree>): void {
    this.authService.getUserDetails().pipe(first()).subscribe({
      next: (userInfo: any) => {
        if (userInfo?.body.items?.length > 0) {
          this.handleValidUser(userInfo, observer)
        } else {
          this.handleInvalidUser(observer)
        }
      }
    })
  }

  private handleValidUser (userInfo: any, observer: Observer<boolean | UrlTree>): void {
    this.stateService.userDetails.next(userInfo.body.items[0])
    const referrerUrl = decodeURIComponent(this.authService.getCookie('referrerUrl') || '')
    const documentReferrer = document.referrer || ''

    if (this.shouldRedirectToMyTests(referrerUrl, documentReferrer)) {
      this.redirectToMyTests(referrerUrl || documentReferrer, observer)
    } else if (this.ssoRedirect) {
      this.ssoRedirectTo(referrerUrl, observer)
    } else {
      this.redirectToDashboard(observer)
    }
  }

  private shouldRedirectToMyTests (referrerUrl: string, documentReferrer: string): boolean {
    return (referrerUrl && referrerUrl.includes('my-tests#')) ||
           (!referrerUrl && documentReferrer.includes('my-tests#'))
  }

  private redirectToMyTests (url: string, observer: Observer<boolean | UrlTree>): void {
    this.authService.deleteCookie?.('referrerUrl')
    window.location.replace(url)
    observer.next(false)
    observer.complete()
  }
  private ssoRedirectTo (url: string, observer: Observer<boolean | UrlTree>): void {
    this.ssoRedirect = false
    this.authService.deleteCookie?.('referrerUrl')
    window.location.replace(url)
    observer.next(false)
    observer.complete()
  }
  private redirectToDashboard (observer: Observer<boolean | UrlTree>): void {
    this.loaderService.hide()
    observer.next(this.router.parseUrl('/dashboard'))
    observer.complete()
  }

  private handleInvalidUser (observer: Observer<boolean | UrlTree>): void {
    this.redirectRegister = true
    this.loaderService.hide()
    observer.next(this.router.parseUrl('/register'))
    observer.complete()
  }

  private handleNoToken (state: RouterStateSnapshot, observer: Observer<boolean | UrlTree>): void {
    this.loaderService.hide()
    const shouldRedirect = state.url !== '/login-initiate'
    observer.next(shouldRedirect ? this.router.parseUrl('/login-initiate') : true)
    observer.complete()
  }

  private handleAuthError (state: RouterStateSnapshot, observer: Observer<boolean | UrlTree>): void {
    this.handleNoToken(state, observer)
  }
}
